import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { GuideLightbox } from './GuideLightbox';
import { guideTokens } from '../theme/guideTokens';

interface GuideFigureProps {
  src: string;
  alt: string;
  caption: string;
}

export const GuideFigure: React.FC<GuideFigureProps> = ({ src, alt, caption }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        component="figure"
        sx={{
          m: 0,
          mt: 2,
          p: { xs: 1, sm: 1.5 },
          borderRadius: `${guideTokens.radiusFigure}px`,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff'),
          boxShadow: (t) =>
            t.palette.mode === 'dark' ? 'none' : '0 8px 28px rgba(15, 17, 17, 0.08)',
          overflow: 'hidden',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Enlarge: ${alt}`}
          sx={{
            all: 'unset',
            cursor: 'zoom-in',
            display: 'block',
            width: '100%',
            position: 'relative',
            '&:focus-visible': {
              outline: `2px solid ${guideTokens.accent}`,
              outlineOffset: 2,
              borderRadius: 1.5,
            },
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              maxHeight: { xs: 280, sm: 420 },
              objectFit: 'contain',
              objectPosition: 'top',
              borderRadius: 1.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa'),
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: guideTokens.fontDisplay,
            }}
          >
            <ZoomInIcon sx={{ fontSize: 14 }} />
            Tap to zoom
          </Box>
        </Box>
        <Typography
          component="figcaption"
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.25,
            px: 0.5,
            color: 'text.secondary',
            lineHeight: 1.45,
            fontFamily: guideTokens.fontBody,
          }}
        >
          {caption}
        </Typography>
      </Box>

      <GuideLightbox
        open={open}
        src={src}
        alt={alt}
        caption={caption}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
